import { Box, VStack, Text, HStack } from '@chakra-ui/react'
import { NavLink } from 'react-router-dom'
import type { Database } from '@/shared/types/database'
import {
  Users,
  CreditCard,
  Bell,
  UserCircle,
  FileText,
  Building2,
  Globe,
  Plug,
} from 'lucide-react'
import { useAuth } from '@/features/auth/hooks/useAuth'

type UserRole = Database['public']['Enums']['user_role']

interface SettingsNavItem {
  to: string
  icon: React.ElementType
  label: string
  description: string
  allowedRoles: UserRole[]
}

const navItems: SettingsNavItem[] = [
  {
    to: '/app/settings/users',
    icon: Users,
    label: 'Utilisateurs',
    description: 'Gérer les membres de l\'équipe',
    allowedRoles: ['ADMIN', 'MANAGER'],
  },
  {
    to: '/app/settings/subscription',
    icon: CreditCard,
    label: 'Abonnement',
    description: 'Formule et facturation',
    allowedRoles: ['ADMIN'],
  },
  {
    to: '/app/settings/notifications',
    icon: Bell,
    label: 'Notifications',
    description: 'Préférences de notifications',
    allowedRoles: ['ADMIN', 'MANAGER', 'USER'],
  },
  {
    to: '/app/settings/profile',
    icon: UserCircle,
    label: 'Mon compte',
    description: 'Informations personnelles',
    allowedRoles: ['ADMIN', 'MANAGER', 'USER'],
  },
  {
    to: '/app/settings/documents',
    icon: FileText,
    label: 'Documents',
    description: 'Modèles et courriers',
    allowedRoles: ['ADMIN', 'MANAGER', 'USER'],
  },
  {
    to: '/app/settings/agency',
    icon: Building2,
    label: 'Agence',
    description: 'Nom, thème, branding',
    allowedRoles: ['ADMIN'],
  },
  {
    to: '/app/settings/extranet',
    icon: Globe,
    label: 'Extranet',
    description: 'Configuration portail',
    allowedRoles: ['ADMIN', 'MANAGER'],
  },
  {
    to: '/app/settings/integrations',
    icon: Plug,
    label: 'Intégrations',
    description: 'Outils externes (AR24, visio...)',
    allowedRoles: ['ADMIN'],
  },
]

export function SettingsNav() {
  const { data: auth } = useAuth()

  // Get user's highest role from residences (simplified - takes first residence's role)
  const userRole = auth?.residences?.[0]?.role || 'USER'

  // Filter items based on user role
  const visibleItems = navItems.filter(item =>
    item.allowedRoles.includes(userRole as UserRole)
  )

  return (
    <VStack align="stretch" gap={1} w="full">
      {visibleItems.map((item) => {
        const Icon = item.icon
        return (
          <NavLink
            key={item.to}
            to={item.to}
            style={{ textDecoration: 'none' }}
          >
            {({ isActive }) => (
              <Box
                px={4}
                py={3}
                borderRadius="md"
                bg={isActive ? 'brand.50' : 'transparent'}
                borderLeft="3px solid"
                borderColor={isActive ? 'brand.800' : 'transparent'}
                _hover={{
                  bg: isActive ? 'brand.50' : 'gray.50',
                }}
                transition="all 0.2s"
                cursor="pointer"
              >
                <HStack gap={3} align="start">
                  <Box color={isActive ? 'brand.800' : 'gray.600'} mt={0.5}>
                    <Icon size={20} />
                  </Box>
                  <VStack align="start" gap={0} flex={1}>
                    <Text
                      fontWeight={isActive ? 'semibold' : 'medium'}
                      color={isActive ? 'brand.800' : 'gray.900'}
                      fontSize="sm"
                    >
                      {item.label}
                    </Text>
                    <Text fontSize="xs" color="gray.600" lineHeight="1.3">
                      {item.description}
                    </Text>
                  </VStack>
                </HStack>
              </Box>
            )}
          </NavLink>
        )
      })}
    </VStack>
  )
}
